import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createElement, Suspense, useCallback, useEffect, useReducer, useState, } from "react";
import { ExtensionManager } from "./extensibility/extensionManager";
import { ServiceContainer } from "./modularity/serviceContainer";
import { SettingsStore, SettingsStoreIdentity } from "./services/settingsStore";
import { MakeShellServiceDefinition, RootComponentServiceIdentity } from "./services/shellService";
import { ThemeModeSettingDescriptor, ThemeServiceDefinition } from "./services/themeService";
import { Body1, Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, List, ListItem, makeStyles, Spinner, tokens, } from "@fluentui/react-components";
import { CheckmarkCircleRegular, ErrorCircleRegular, InfoRegular, WarningRegular } from "@fluentui/react-icons";
import { createRoot } from "react-dom/client";
import { Deferred } from "core/Misc/deferred";
import { Logger } from "core/Misc/logger";
import { ToastProvider } from "shared-ui-components/fluent/primitives/toast";
import { DialogServiceIdentity } from "./services/dialogService";
import { ToastServiceIdentity } from "./services/toastService";
import { Theme } from "./components/theme";
import { DialogContext } from "./contexts/dialogContext";
import { ExtensionManagerContext } from "./contexts/extensionManagerContext";
import { SettingsStoreContext } from "./contexts/settingsContext";
import { TeachingMomentsContext } from "./contexts/teachingMomentsContext";
import { ReactContextServiceIdentity } from "./services/reactContextService";
import { ThemeSelectorServiceDefinition } from "./services/themeSelectorService";
const useStyles = makeStyles({
    app: {
        colorScheme: "light dark",
        flexGrow: 1,
        display: "flex",
        flexDirection: "column",
        backgroundColor: tokens.colorTransparentBackground,
    },
    spinner: {
        flexGrow: 1,
        animationDuration: "1s",
        animationName: {
            from: { opacity: 0 },
            to: { opacity: 1 },
        },
    },
    extensionErrorTitleDiv: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: tokens.spacingHorizontalS,
    },
    extensionErrorIcon: {
        color: tokens.colorPaletteRedForeground1,
    },
    dialogTitle: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: tokens.spacingHorizontalS,
    },
    dialogIconSuccess: {
        color: tokens.colorStatusSuccessForeground1,
    },
    dialogIconError: {
        color: tokens.colorStatusDangerForeground1,
    },
    dialogIconWarning: {
        color: tokens.colorStatusWarningForeground1,
    },
    dialogIconInfo: {
        color: tokens.colorBrandForeground1,
    },
});
const ReactContextsWrapper = ({ contexts, children }) => {
    return _jsx(_Fragment, { children: contexts.reduceRight((acc, entry) => createElement(entry.provider, { value: entry.value }, acc), children) });
};
/**
 * Creates a modular tool with a base set of common tool services, including the toolbar/side pane basic UI layout.
 * @param options The options for the tool.
 * @returns A token that can be used to dispose of the tool.
 */
export function MakeModularTool(options) {
    const { namespace, containerElement, serviceDefinitions, themeMode, showThemeSelector = true, extensionFeeds = [], parentContainer, disableTeachingMoments = false } = options;
    const teachingMomentsContextValue = { disabled: disableTeachingMoments };
    // Create the settings store immediately as it will be exposed to services and through React context.
    const settingsStore = new SettingsStore(namespace);
    // If a theme mode is provided, just write the setting so it is the active theme.
    if (themeMode) {
        settingsStore.writeSetting(ThemeModeSettingDescriptor, themeMode);
    }
    // This deferred resolves once the React effect cleanup (which disposes the ServiceContainer) is complete.
    const disposeDeferred = new Deferred();
    const modularToolRootComponent = () => {
        const classes = useStyles();
        const [requiredExtensions, setRequiredExtensions] = useState();
        const [requiredExtensionsDeferred, setRequiredExtensionsDeferred] = useState();
        const [extensionInstallError, setExtensionInstallError] = useState();
        const [toastHandle, setToastHandle] = useState(null);
        const [toastQueue, setToastQueue] = useState([]);
        useEffect(() => {
            if (toastHandle && toastQueue.length > 0) {
                for (const { message, options } of toastQueue) {
                    toastHandle.showToast(message, options);
                }
                setToastQueue([]);
            }
        }, [toastHandle, toastQueue]);
        // Queue of dialogs to display. We show one at a time (the one at the head of the queue).
        const [dialogQueue, dispatchDialogQueue] = useReducer((state, action) => {
            switch (action.type) {
                case "enqueue":
                    return [...state, action.options];
                case "dequeue":
                    return state.slice(1);
            }
        }, []);
        const showDialog = useCallback((dialogOptions) => {
            dispatchDialogQueue({ type: "enqueue", options: dialogOptions });
        }, []);
        const onDismissDialog = useCallback(() => {
            dispatchDialogQueue({ type: "dequeue" });
        }, []);
        const [rootComponentService, setRootComponentService] = useState();
        const [contexts, updateContexts] = useReducer((state, action) => {
            switch (action.type) {
                case "add":
                    return [...state, action.entry].sort((a, b) => a.order - b.order);
                case "remove":
                    return state.filter((e) => e.provider !== action.provider);
                case "update":
                    return state.map((e) => (e.provider === action.provider ? { ...e, value: action.value } : e));
            }
        }, [
            // Static contexts seeded into the wrapper so they don't add JSX nesting below.
            // Negative orders keep them as the outermost providers (lowest order = outermost via reduceRight).
            { provider: TeachingMomentsContext.Provider, value: teachingMomentsContextValue, order: -2 },
            { provider: SettingsStoreContext.Provider, value: settingsStore, order: -1 },
            { provider: ExtensionManagerContext.Provider, value: undefined, order: 0 },
        ]);
        // This is the main async initialization.
        useEffect(() => {
            const initializeExtensionManagerAsync = async () => {
                const serviceContainer = new ServiceContainer("ModularToolContainer", parentContainer);
                // Expose the settings store as a service so other services can read/write settings.
                serviceContainer.addService({
                    friendlyName: "Settings Store",
                    produces: [SettingsStoreIdentity],
                    factory: () => settingsStore,
                });
                // Expose the react context service so other services can add React context providers.
                serviceContainer.addService({
                    friendlyName: "React Context Service",
                    produces: [ReactContextServiceIdentity],
                    factory: () => ({
                        addContext(provider, initialValue, options) {
                            updateContexts({ type: "add", entry: { provider: provider, value: initialValue, order: options?.order ?? 0 } });
                            return {
                                updateValue: (newValue) => {
                                    updateContexts({ type: "update", provider: provider, value: newValue });
                                },
                                dispose: () => {
                                    updateContexts({ type: "remove", provider: provider });
                                },
                            };
                        },
                    }),
                });
                // Expose the toast service so non-React code (e.g. Observable callbacks) can show toasts.
                serviceContainer.addService({
                    friendlyName: "Toast Service",
                    produces: [ToastServiceIdentity],
                    factory: () => ({
                        showToast: (message, options) => {
                            setToastQueue((prev) => [...prev, { message, options }]);
                        },
                    }),
                });
                // Expose the dialog service so non-React code (e.g. Observable callbacks) can show dialogs.
                serviceContainer.addService({
                    friendlyName: "Dialog Service",
                    produces: [DialogServiceIdentity],
                    factory: () => ({ showDialog }),
                });
                // Register the shell service (top level toolbar/side pane UI layout).
                serviceContainer.addService(MakeShellServiceDefinition(options));
                // Register a service that simply consumes the services we need before first render.
                serviceContainer.addService({
                    friendlyName: "Service Bootstrapper",
                    consumes: [RootComponentServiceIdentity],
                    factory: (rootComponent) => {
                        // Use function syntax for the state setter since the root component may be a function component.
                        setRootComponentService(() => rootComponent);
                        return {
                            dispose: () => setRootComponentService(undefined),
                        };
                    },
                });
                // Register the theme service (exposes the current theme to other services).
                serviceContainer.addService(ThemeServiceDefinition);
                // Register the theme selector service (for selecting the theme) if theming is configured.
                if (showThemeSelector) {
                    serviceContainer.addService(ThemeSelectorServiceDefinition);
                }
                // Register the extension list service (for browsing/installing extensions) if extension feeds are provided.
                if (extensionFeeds.length > 0) {
                    const { ExtensionListServiceDefinition } = await import("./services/extensionsListService");
                    serviceContainer.addService(ExtensionListServiceDefinition);
                }
                // Register all external services (that make up a unique tool).
                serviceContainer.addServices(...serviceDefinitions);
                // Create the extension manager, passing along the registry for runtime changes to the registered services.
                const extensionManager = await ExtensionManager.CreateAsync(namespace, serviceContainer, extensionFeeds, setExtensionInstallError);
                // Check query params for required extensions. This lets users share links with sets of extensions.
                const queryParams = new URLSearchParams(window.location.search);
                const requiredExtensions = queryParams.getAll("babylon.requiredExtension");
                const uninstalledExtensions = [];
                for (const requiredExtension of requiredExtensions) {
                    // These could possibly be parallelized to speed things up, but it's more complex so let's wait and see if we need it.
                    // eslint-disable-next-line no-await-in-loop
                    const query = await extensionManager.queryExtensionsAsync(requiredExtension);
                    // eslint-disable-next-line no-await-in-loop
                    const extensions = await query.getExtensionsAsync(0, query.totalCount);
                    for (const extension of extensions) {
                        if (!extension.isInstalled) {
                            uninstalledExtensions.push(extension);
                        }
                    }
                }
                // Check if any required extensions are uninstalled or disabled. If so, show a dialog to the user.
                if (uninstalledExtensions.length > 0) {
                    setRequiredExtensions(uninstalledExtensions.map((extension) => extension.metadata.name));
                    const deferred = new Deferred();
                    setRequiredExtensionsDeferred(deferred);
                    if (await deferred.promise) {
                        for (const extension of uninstalledExtensions) {
                            // This could possibly be parallelized to speed things up, but it's more complex so let's wait and see if we need it.
                            // eslint-disable-next-line no-await-in-loop
                            await extension.installAsync();
                        }
                    }
                }
                // Set the contexts.
                updateContexts({ type: "update", provider: ExtensionManagerContext.Provider, value: { extensionManager } });
                return () => {
                    extensionManager.dispose();
                    serviceContainer.dispose();
                    serviceContainer.dispose();
                };
            };
            const disposePromise = initializeExtensionManagerAsync();
            return () => {
                disposePromise
                    // eslint-disable-next-line github/no-then
                    .then((dispose) => dispose())
                    // eslint-disable-next-line github/no-then
                    .catch((error) => {
                    Logger.Error(`Failed to dispose of the modular tool: ${error}`);
                })
                    .finally(() => disposeDeferred.resolve());
            };
        }, []);
        const onAcceptRequiredExtensions = useCallback(() => {
            setRequiredExtensions(undefined);
            requiredExtensionsDeferred?.resolve(true);
        }, [setRequiredExtensions, requiredExtensionsDeferred]);
        const onRejectRequiredExtensions = useCallback(() => {
            setRequiredExtensions(undefined);
            requiredExtensionsDeferred?.resolve(false);
        }, [setRequiredExtensions, requiredExtensionsDeferred]);
        const onAcknowledgedExtensionInstallError = useCallback(() => {
            setExtensionInstallError(undefined);
        }, [setExtensionInstallError]);
        // The dialog at the head of the queue, if any, is the one currently being displayed.
        const currentDialog = dialogQueue[0];
        const currentDialogIcon = (() => {
            switch (currentDialog?.intent) {
                case "success":
                    return _jsx(CheckmarkCircleRegular, { className: classes.dialogIconSuccess });
                case "error":
                    return _jsx(ErrorCircleRegular, { className: classes.dialogIconError });
                case "warning":
                    return _jsx(WarningRegular, { className: classes.dialogIconWarning });
                case "info":
                case undefined:
                    return _jsx(InfoRegular, { className: classes.dialogIconInfo });
            }
        })();
        // Show a spinner until a main view has been set.
        if (!rootComponentService) {
            return (_jsx(ReactContextsWrapper, { contexts: contexts, children: _jsx(Theme, { className: classes.app, targetDocument: targetDocument, children: _jsx(Spinner, { className: classes.spinner }) }) }));
        }
        else {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            const Content = rootComponentService.rootComponent;
            return (_jsx(ReactContextsWrapper, { contexts: contexts, children: _jsx(Theme, { className: classes.app, targetDocument: targetDocument, children: _jsxs(ToastProvider, { imperativeRef: setToastHandle, children: [_jsx(Dialog, { open: !!requiredExtensions, modalType: "alert", children: _jsx(DialogSurface, { children: _jsxs(DialogBody, { children: [_jsx(DialogTitle, { children: "Required Extensions" }), _jsxs(DialogContent, { children: ["Opening this URL requires the following extensions to be installed and enabled:", _jsx("ul", { children: requiredExtensions?.map((name) => (_jsx("li", { children: name }, name))) })] }), _jsxs(DialogActions, { children: [_jsx(Button, { appearance: "primary", onClick: onAcceptRequiredExtensions, children: "Install & Enable" }), _jsx(Button, { appearance: "secondary", onClick: onRejectRequiredExtensions, children: "No Thanks" })] })] }) }) }), _jsx(Dialog, { open: !!extensionInstallError, modalType: "alert", children: _jsx(DialogSurface, { children: _jsxs(DialogBody, { children: [_jsx(DialogTitle, { children: _jsxs("div", { className: classes.extensionErrorTitleDiv, children: ["Extension Install Error", _jsx(ErrorCircleRegular, { className: classes.extensionErrorIcon })] }) }), _jsx(DialogContent, { children: _jsxs(List, { children: [_jsx(ListItem, { children: _jsx(Body1, { children: `Extension "${extensionInstallError?.extension.name}" failed to install and was removed.` }) }), _jsx(ListItem, { children: _jsx(Body1, { children: `${extensionInstallError?.error}` }) })] }) }), _jsx(DialogActions, { children: _jsx(Button, { appearance: "primary", onClick: onAcknowledgedExtensionInstallError, children: "Close" }) })] }) }) }), _jsx(Dialog, { open: !!currentDialog, modalType: "alert", children: _jsx(DialogSurface, { children: _jsxs(DialogBody, { children: [_jsx(DialogTitle, { children: _jsxs("div", { className: classes.dialogTitle, children: [currentDialogIcon, currentDialog?.title] }) }), currentDialog?.content && _jsx(DialogContent, { children: currentDialog.content }), _jsx(DialogActions, { children: _jsx(Button, { appearance: "primary", onClick: onDismissDialog, children: "OK" }) })] }) }) }), _jsx(Suspense, { fallback: _jsx(Spinner, { className: classes.spinner }), children: _jsx(DialogContext.Provider, { value: { showDialog }, children: _jsx(Content, {}) }) })] }) }) }));
        }
    };
    // Derive the target document from the container element. When the container is in a popup
    // window (or other document distinct from the main one), this is what makes Fluent inject
    // styles and render portals into the correct document.
    const containerOwnerDocument = containerElement.ownerDocument;
    const targetDocument = containerOwnerDocument && containerOwnerDocument !== document ? containerOwnerDocument : undefined;
    // Set the container element to be a flex container so that the tool can be displayed properly.
    const originalContainerElementDisplay = containerElement.style.display;
    containerElement.style.display = "flex";
    // Create and render the react root component.
    const reactRoot = createRoot(containerElement);
    reactRoot.render(createElement(modularToolRootComponent));
    let disposed = false;
    return {
        // eslint-disable-next-line @typescript-eslint/promise-function-async
        dispose: () => {
            // Unmount and restore the original container element display.
            if (!disposed) {
                disposed = true;
                reactRoot.unmount();
                containerElement.style.display = originalContainerElementDisplay;
            }
            // The promise resolves once the React effect cleanup
            // (which disposes the ServiceContainer) has completed.
            return disposeDeferred.promise;
        },
    };
}
//# sourceMappingURL=modularTool.js.map